import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Bot,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  DollarSign,
  Gift,
  GitBranchPlus,
  Languages,
  HeartPulse,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MessageSquare,
  Newspaper,
  PackageCheck,
  Phone,
  Route,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react';
import { usePath } from '../hooks/usePath';
import { navigate } from '../lib/navigation';

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'General',
    items: [
      { label: 'Overview', icon: LayoutDashboard, path: '/' },
      { label: 'Health', icon: HeartPulse, path: '/platform-health' },
    ],
  },
  {
    title: 'Organization',
    items: [
      { label: 'Organizations', icon: Building2, path: '/organizations' },
      { label: 'Subscriptions', icon: CreditCard, path: '/subscriptions' },
      { label: 'Free Capacity', icon: Gift, path: '/free-capacity' },
      { label: 'Regulatory Approvals', icon: ShieldCheck, path: '/regulatory-approvals' },
      { label: 'Voice Language Mappings', icon: Languages, path: '/admin-voice-language-mappings' },
    ],
  },
  {
    title: 'AI Agents',
    items: [
      { label: 'Pipeline', icon: GitBranchPlus, path: '/pipeline-agents' },
      { label: 'Prompts', icon: Bot, path: '/prompts' },
      { label: 'Tools', icon: Wrench, path: '/agent-tools' },
      { label: 'Model Routes', icon: Route, path: '/model-routes' },
      { label: 'Monitoring', icon: Activity, path: '/agent-monitoring' },
      { label: 'Workflows', icon: Workflow, path: '/workflows' },
      { label: 'Chat', icon: MessageSquare, path: '/chat-dashboard' },
      { label: 'Voice Calls', icon: Phone, path: '/voice-calls' },
      { label: 'Lead Timeline', icon: ListChecks, path: '/lead-timeline' },
      { label: 'Costs', icon: DollarSign, path: '/costs' },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Users', icon: Users, path: '/users' },
      { label: 'Waitlist', icon: ClipboardList, path: '/waitlist' },
      { label: 'Releases', icon: PackageCheck, path: '/releases' },
      { label: 'Changelog', icon: Megaphone, path: '/changelog' },
      { label: 'Blog', icon: Newspaper, path: '/blog' },
      { label: 'Audit Log', icon: ScrollText, path: '/audit-log' },
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const currentPath = usePath();

  function isActive(path: string) {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  }

  return (
    <aside
      className={`flex flex-col bg-surface border-r border-border transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}
    >
      <div className="flex items-center h-14 px-3 border-b border-border">
        {!collapsed && (
          <span className="text-sm font-semibold text-text whitespace-nowrap">
            FlowSellAI Admin
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="ml-auto p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {navSections.map((section) => (
          <div key={section.title} className="mb-3">
            {!collapsed && (
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                {section.title}
              </p>
            )}
            {collapsed && <div className="border-t border-border mx-2 my-1.5" />}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    type="button"
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-[13px] transition-colors ${
                      active
                        ? 'bg-primary-muted text-primary'
                        : 'text-text-secondary hover:text-text hover:bg-surface-hover'
                    } ${collapsed ? 'justify-center' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon size={18} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
